/**
 * Проверяет, является ли расписание заполненным (содержит хотя бы один временной интервал)
 * @param schedule - Расписание в формате { "wed": ["00:30-02:30"], "thu": ["02:30-04:00"], ... }
 * @returns {boolean} - true, если расписание содержит хотя бы один интервал
 */
export function isScheduleFilled(schedule: any): boolean {
    if (!schedule || typeof schedule !== 'object') {
        return false;
    }

    for (const day in schedule) {
        const intervals = schedule[day];
        if (Array.isArray(intervals) && intervals.length > 0) {
            return true;
        }
    }

    return false;
}

/**
 * Проверяет, принадлежит ли текущее время (или заданное время) к выбранному расписанию
 * @param schedule - Расписание в формате { "wed": ["00:30-02:30"], "thu": ["02:30-04:00"], ... }
 * @param checkTime - Время для проверки (по умолчанию текущее время)
 * @param options - Дополнительные опции
 * @returns {boolean} - true, если время попадает в расписание
 */
export function isTimeInSchedule(
    schedule: Record<string, string[]>,
    checkTime?: Date,
    options: {
        dayMap?: Record<number, string>;
        strictMode?: boolean;
    } = {}
): boolean {
    // Время для проверки (по умолчанию текущее)
    const timeToCheck = checkTime || new Date();

    // Маппинг дней недели (по умолчанию английские сокращения как в компоненте)
    const defaultDayMap: Record<number, string> = {
        0: 'sun', // Воскресенье
        1: 'mon', // Понедельник
        2: 'tue', // Вторник
        3: 'wed', // Среда
        4: 'thu', // Четверг
        5: 'fri', // Пятница
        6: 'sat'  // Суббота
    };

    const dayMap = options.dayMap || defaultDayMap;
    const strictMode = options.strictMode !== undefined ? options.strictMode : false;

    // Получаем текущий день недели
    const dayOfWeek = timeToCheck.getDay();
    const dayKey = dayMap[dayOfWeek];

    // Если день недели не найден в маппинге
    if (!dayKey) {
        console.warn(`Не найден маппинг для дня недели: ${dayOfWeek}`);
        return false;
    }

    // Если расписание не содержит записей для этого дня
    if (!schedule[dayKey] || schedule[dayKey].length === 0) {
        return false;
    }

    // Преобразуем текущее время в минуты от начала дня
    const currentHours = timeToCheck.getHours();
    const currentMinutes = timeToCheck.getMinutes();
    const currentTotalMinutes = currentHours * 60 + currentMinutes;

    // Проверяем все диапазоны для текущего дня
    for (const timeRange of schedule[dayKey]) {
        if (isTimeInRange(currentTotalMinutes, timeRange, strictMode)) {
            return true;
        }
    }

    return false;
}

/**
 * Проверяет, находится ли время (в минутах) в указанном диапазоне
 * @param timeInMinutes - Время в минутах от начала дня (0-1440)
 * @param timeRange - Диапазон времени в формате "HH:MM-HH:MM"
 * @param strictMode - Строгий режим (исключает конечную границу)
 * @returns {boolean} - true, если время попадает в диапазон
 */
export function isTimeInRange(
    timeInMinutes: number,
    timeRange: string,
    strictMode: boolean = false
): boolean {
    // Парсим диапазон времени
    const [startStr, endStr] = timeRange.split('-');

    if (!startStr || !endStr) {
        console.warn(`Некорректный формат диапазона времени: ${timeRange}`);
        return false;
    }

    // Преобразуем строки времени в минуты
    const startMinutes = timeStringToMinutes(startStr);
    const endMinutes = timeStringToMinutes(endStr);

    if (startMinutes === null || endMinutes === null) {
        return false;
    }

    // Проверяем разные сценарии в зависимости от диапазона

    // 1. Обычный диапазон (start < end)
    if (startMinutes < endMinutes) {
        if (strictMode) {
            return timeInMinutes >= startMinutes && timeInMinutes < endMinutes;
        } else {
            return timeInMinutes >= startMinutes && timeInMinutes <= endMinutes;
        }
    }

    // 2. Диапазон проходит через полночь (например, "23:00-01:00")
    // В этом случае диапазон охватывает время от start до конца дня (1440 минут)
    // и от начала дня (0 минут) до end
    if (startMinutes > endMinutes) {
        if (strictMode) {
            return (timeInMinutes >= startMinutes && timeInMinutes < 1440) ||
                (timeInMinutes >= 0 && timeInMinutes < endMinutes);
        } else {
            return (timeInMinutes >= startMinutes && timeInMinutes <= 1440) ||
                (timeInMinutes >= 0 && timeInMinutes <= endMinutes);
        }
    }

    // 3. Начало и конец совпадают (нулевой интервал)
    return timeInMinutes === startMinutes;
}

/**
 * Преобразует строку времени в минуты от начала дня
 * @param timeString - Строка времени в формате "HH:MM" или "HH:MM:SS"
 * @returns {number | null} - Количество минут или null при ошибке
 */
export function timeStringToMinutes(timeString: string): number | null {
    // Убираем возможные пробелы
    const cleaned = timeString.trim();

    // Разбиваем на часы и минуты
    const parts = cleaned.split(':');

    if (parts.length < 2) {
        console.warn(`Некорректный формат времени: ${timeString}`);
        return null;
    }

    const hours = parseInt(parts[0]!, 10);
    const minutes = parseInt(parts[1]!, 10);

    // Проверяем корректность
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 24 || minutes < 0 || minutes > 59) {
        console.warn(`Некорректное значение времени: ${timeString}`);
        return null;
    }

    // 24:00 преобразуем в 1440 минут
    if (hours === 24 && minutes === 0) {
        return 1440;
    }

    return hours * 60 + minutes;
}

/**
 * Получает следующий активный временной диапазон из расписания
 * @param schedule - Расписание
 * @param fromTime - Время, от которого ищем (по умолчанию текущее)
 * @param dayMap - Маппинг дней недели
 * @returns {object | null} - Следующий диапазон или null
 */
export function getNextTimeSlot(
    schedule: Record<string, string[]>,
    fromTime?: Date,
    dayMap?: Record<number, string>
): {
    day: string;
    timeRange: string;
    startTime: Date;
    endTime: Date;
} | null {
    const checkTime = fromTime || new Date();
    const defaultDayMap: Record<number, string> = {
        0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat'
    };

    const usedDayMap = dayMap || defaultDayMap;
    const currentDayOfWeek = checkTime.getDay();

    // Ищем во все дни, начиная с текущего
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const targetDayOfWeek = (currentDayOfWeek + dayOffset) % 7;
        const dayKey = usedDayMap[targetDayOfWeek]!;

        if (!schedule[dayKey] || schedule[dayKey].length === 0) {
            continue;
        }

        // Для каждого диапазона в этом дне
        for (const timeRange of schedule[dayKey]) {
            const [startStr] = timeRange.split('-');
            const startMinutes = timeStringToMinutes(startStr!);

            if (startMinutes === null) continue;

            // Создаем дату начала диапазона
            const startDate = new Date(checkTime);
            startDate.setDate(startDate.getDate() + dayOffset);
            startDate.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);

            // Если это сегодня и время уже прошло, пропускаем
            if (dayOffset === 0 && startDate < checkTime) {
                continue;
            }

            // Парсим конец диапазона
            const [, endStr] = timeRange.split('-');
            const endMinutes = timeStringToMinutes(endStr!);

            if (endMinutes === null) continue;

            const endDate = new Date(startDate);
            // Учитываем переход через полночь
            if (endMinutes < startMinutes) {
                endDate.setDate(endDate.getDate() + 1);
            }
            endDate.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);

            return {
                day: dayKey,
                timeRange,
                startTime: startDate,
                endTime: endDate
            };
        }
    }

    return null;
}

/**
 * Проверяет, активен ли сейчас какой-либо диапазон и возвращает его
 * @param schedule - Расписание
 * @param checkTime - Время для проверки
 * @param dayMap - Маппинг дней недели
 * @returns {object | null} - Активный диапазон или null
 */
export function getCurrentTimeSlot(
    schedule: Record<string, string[]>,
    checkTime?: Date,
    dayMap?: Record<number, string>
): {
    day: string;
    timeRange: string;
    remainingMinutes: number;
} | null {
    const timeToCheck = checkTime || new Date();
    const defaultDayMap: Record<number, string> = {
        0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat'
    };

    const usedDayMap = dayMap || defaultDayMap;
    const dayOfWeek = timeToCheck.getDay();
    const dayKey = usedDayMap[dayOfWeek]!;

    if (!schedule[dayKey] || schedule[dayKey].length === 0) {
        return null;
    }

    const currentTotalMinutes = timeToCheck.getHours() * 60 + timeToCheck.getMinutes();

    for (const timeRange of schedule[dayKey]) {
        if (isTimeInRange(currentTotalMinutes, timeRange)) {
            const [, endStr] = timeRange.split('-');
            const endMinutes = timeStringToMinutes(endStr!);

            if (endMinutes === null) continue;

            // Рассчитываем оставшееся время
            let remainingMinutes: number;

            if (endMinutes > currentTotalMinutes) {
                // Обычный диапазон
                remainingMinutes = endMinutes - currentTotalMinutes;
            } else {
                // Диапазон через полночь
                remainingMinutes = (1440 - currentTotalMinutes) + endMinutes;
            }

            return {
                day: dayKey,
                timeRange,
                remainingMinutes
            };
        }
    }

    return null;
}

// Пример использования:
/*
const schedule = {
  "wed": ["00:30-02:30"],
  "thu": ["02:30-04:00"],
  "fri": ["04:00-05:30"]
};

// Проверка текущего времени
const isActiveNow = isTimeInSchedule(schedule);
console.log('Активно сейчас:', isActiveNow);

// Проверка конкретного времени
const testTime = new Date();
testTime.setHours(1, 30, 0, 0); // 01:30
testTime.setDay(3); // Среда
const isActiveAtTestTime = isTimeInSchedule(schedule, testTime);
console.log('Активно в 01:30 среды:', isActiveAtTestTime);

// Получение текущего активного диапазона
const currentSlot = getCurrentTimeSlot(schedule);
if (currentSlot) {
  console.log(`Сейчас активен: ${currentSlot.day} ${currentSlot.timeRange}`);
  console.log(`Осталось минут: ${currentSlot.remainingMinutes}`);
}

// Получение следующего диапазона
const nextSlot = getNextTimeSlot(schedule);
if (nextSlot) {
  console.log(`Следующий диапазон: ${nextSlot.day} ${nextSlot.timeRange}`);
  console.log(`Начнется: ${nextSlot.startTime.toLocaleString()}`);
}
*/

// Также можно использовать русские названия дней:
/*
const russianDayMap = {
  0: 'sun',      // Воскресенье
  1: 'mon',      // Понедельник
  2: 'tue',      // Вторник
  3: 'wed',      // Среда
  4: 'thu',      // Четверг
  5: 'fri',      // Пятница
  6: 'sat'       // Суббота
};

const schedule = {
  "wed": ["09:00-12:00", "14:00-18:00"],
  "thu": ["10:00-13:00", "14:00-17:00"]
};

const isActive = isTimeInSchedule(schedule, new Date(), { dayMap: russianDayMap });
*/
